import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRotateRight, faStore, faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";

import Alert from "../../crmpostventa-shared/components/Alert";
import ChoiceGroup from "../../crmpostventa-shared/components/ChoiceGroup";
import { OrigenChip, StatusChip } from "../components/Chips";
import { EMPTY, ESTADO_LABELS, codeOf, formatCuit, formatDate, formatRollos, formatShortDate } from "../rolloFormat";
import {
  CANAL_OPTIONS,
  LIMITS,
  SIN_ROLLOS_OPTIONS,
  comercioOptionLabel,
  errorId,
  fieldId,
  terminalOptionLabel,
  terminalesDelComercio,
} from "./nuevaGestionModel";

const describedBy = (errors, key) => (errors[key] ? { "aria-invalid": true, "aria-describedby": errorId(key) } : {});

const ErrorText = ({ errors, name }) =>
  errors[name] ? (
    <span className="crm-rollo__err" id={errorId(name)}>
      {errors[name]}
    </span>
  ) : null;

function Field({ name, label, htmlFor, labelId, required = false, optional = false, full = false, errors, help, children }) {
  const LabelTag = htmlFor ? "label" : "span";
  return (
    <div className={`crm-rollo__fld${full ? " crm-rollo__fld--full" : ""}${errors[name] ? " has-error" : ""}`}>
      <LabelTag className="crm-rollo__fld-label" htmlFor={htmlFor} id={labelId}>
        {label}
        {required ? (
          <span className="crm-rollo__req" aria-hidden="true">
            {" *"}
          </span>
        ) : null}
        {optional ? <span className="crm-rollo__opt"> (opcional)</span> : null}
      </LabelTag>
      {children}
      {errors[name] ? <ErrorText errors={errors} name={name} /> : help ? <span className="crm-rollo__help">{help}</span> : null}
    </div>
  );
}

// Header of step 2 with the client and "Cambiar cliente" (back to step 1 with the client still selected).
export function ClienteHeader({ cliente, disabled, onChangeCliente }) {
  const detail = [
    cliente?.cuit ? `CUIT ${formatCuit(cliente.cuit)}` : "",
    cliente?.legajo ? `Legajo ${cliente.legajo}` : "",
    cliente?.razonSocial || "",
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="crm-rollo__client">
      <div>
        <div className="crm-rollo__rcard-name">{cliente?.nombre || cliente?.razonSocial || "Sin nombre"}</div>
        {detail ? <span className="crm-rollo__s">{detail}</span> : null}
      </div>
      <button type="button" className="crm-rollo__link" disabled={disabled} onClick={onChangeCliente}>
        Cambiar cliente
      </button>
    </div>
  );
}

export function ContextoLoading() {
  return (
    <div className="crm-rollo__context-skel" aria-busy="true" aria-label="Cargando datos del cliente">
      {["55%", "80%", "40%", "70%", "60%"].map((width) => (
        <span key={width} className="crm-rollo__skel crm-rollo__skel--tall" style={{ width }} aria-hidden="true" />
      ))}
    </div>
  );
}

export function ContextoError({ message, onRetry }) {
  return (
    <div className="crm-rollo__note crm-rollo__note--error" role="alert">
      <span className="crm-rollo__note-ico" aria-hidden="true">
        <FontAwesomeIcon icon={faTriangleExclamation} />
      </span>
      <b>No pudimos cargar los datos del cliente</b>
      <p>{message || "Probá de nuevo en unos segundos."}</p>
      <button type="button" className="crm-rollo__btn crm-rollo__btn--secondary crm-rollo__btn--sm" onClick={onRetry}>
        <FontAwesomeIcon icon={faRotateRight} aria-hidden="true" />
        Reintentar
      </button>
    </div>
  );
}

/**
 * Comercio of the gestión (S3a), above the terminal. With one comercio it is already chosen and only shown; with
 * several it is a required select, and the contexto (terminals, history, open gestión) waits for the choice.
 */
export function ComercioField({ comercios, value, errors, disabled, onChange }) {
  if (comercios.length <= 1) {
    return (
      <div className="crm-rollo__fld">
        <span className="crm-rollo__fld-label">Comercio</span>
        <span className="crm-rollo__t" id={fieldId("comercio")}>
          {comercios[0] ? comercioOptionLabel(comercios[0]) : EMPTY}
        </span>
      </div>
    );
  }

  return (
    <Field name="comercio" label="Comercio" htmlFor={fieldId("comercio")} required errors={errors}>
      <span className="crm-rollo__field crm-rollo__field--select">
        <select
          className="crm-rollo__select"
          id={fieldId("comercio")}
          value={value}
          disabled={disabled}
          aria-required="true"
          onChange={(event) => onChange(event.target.value)}
          {...describedBy(errors, "comercio")}
        >
          <option value="" disabled>
            Elegí un comercio
          </option>
          {comercios.map((comercio) => (
            <option key={comercio.key} value={comercio.key}>
              {comercioOptionLabel(comercio)}
            </option>
          ))}
        </select>
      </span>
    </Field>
  );
}

// In place of the context block while the client with several comercios has none chosen yet.
function ComercioPendiente() {
  return (
    <div className="crm-rollo__note">
      <span className="crm-rollo__note-ico" aria-hidden="true">
        <FontAwesomeIcon icon={faStore} />
      </span>
      <b>¿Para qué comercio es el pedido?</b>
      <p>Elegilo arriba para ver su última entrega y sus últimas gestiones de rollo.</p>
    </div>
  );
}

// Variant when the comercio already has an open gestión: the warning replaces the form (one open gestión per
// comercio, S3). Estado and date come from the gestión detail when it could be read. With several comercios the
// comercio select stays above, so another comercio of the client can still get its gestión (S3a).
export function GestionAbierta({ abierta, otrosComercios = false, onViewOpen }) {
  const gestion = abierta?.gestion;
  const extra = gestion
    ? ` (${ESTADO_LABELS[codeOf(gestion.estado)] || gestion.estado}, desde el ${formatShortDate(gestion.createdAt)})`
    : "";

  return (
    <>
      <Alert tone="warn" role="alert" title={`Este comercio ya tiene una gestión de rollo abierta${extra}.`}>
        Para crear una nueva, primero tiene que cerrarse esa.
        {otrosComercios ? " Podés elegir otro comercio del cliente." : null}
      </Alert>
      <div>
        <button type="button" className="crm-rollo__btn crm-rollo__btn--secondary" onClick={onViewOpen}>
          Ver gestión abierta
        </button>
      </div>
    </>
  );
}

// Read-only context the asesor needs before asking for data: last delivery and the last 3 closed gestiones.
function Contexto({ contexto }) {
  const ultima = contexto?.ultimaEntrega;
  const gestiones = (contexto?.ultimasGestiones || []).slice(0, 3);

  return (
    <section className="crm-rollo__context" aria-label="Contexto del cliente">
      <div>
        <h4>Última entrega</h4>
        {ultima ? (
          <>
            <div className="crm-rollo__t">{formatDate(ultima.fecha)}</div>
            <span className="crm-rollo__s">
              {[formatRollos(ultima.cantidad), ultima.recibioNombre ? `Recibió ${ultima.recibioNombre}` : ""].filter(Boolean).join(" · ")}
            </span>
          </>
        ) : (
          <span className="crm-rollo__muted">Sin entregas previas</span>
        )}
      </div>
      <div>
        <h4>Últimas gestiones de rollo</h4>
        {gestiones.length ? (
          <ul className="crm-rollo__hist">
            {gestiones.map((gestion) => (
              <li key={gestion.id}>
                <span>{`${formatDate(gestion.fechaCierre)} · ${formatRollos(gestion.cantidadEntregada ?? gestion.cantidadSolicitada)}`}</span>
                <StatusChip estado={gestion.estado} />
              </li>
            ))}
          </ul>
        ) : (
          <span className="crm-rollo__muted">Sin gestiones anteriores</span>
        )}
      </div>
    </section>
  );
}

// What the system fills in by itself, shown without fields (dashed box).
function DatosAutomaticos({ asesorNombre, today }) {
  return (
    <section className="crm-rollo__auto" aria-label="Datos automáticos">
      <div className="crm-rollo__auto-title">Se completa automáticamente</div>
      <dl>
        <div>
          <dt>Origen</dt>
          <dd>
            <OrigenChip origen="ASESOR" />
          </dd>
        </div>
        <div>
          <dt>Asesor</dt>
          <dd>{asesorNombre || EMPTY}</dd>
        </div>
        <div>
          <dt>Fecha de creación</dt>
          <dd>{formatDate(today)}</dd>
        </div>
        <div>
          <dt>Estado inicial</dt>
          <dd>
            <StatusChip estado="SOLICITADO" />
          </dd>
        </div>
      </dl>
    </section>
  );
}

/**
 * Step 2 · Datos de la gestión (design RolloNewSelect). The terminal is optional everywhere (S3), so it is a
 * select with "Sin terminal" even when the comercio has one or several; it lists only the terminals of the chosen
 * comercio (S3a). Without contexto (no comercio chosen yet) a hint takes the place of the context block.
 * Validation runs on submit (in NuevaGestionModal); each message goes under its field and clears as soon as the
 * field changes.
 */
export default function GestionStep({
  contexto,
  numeroComercio,
  form,
  errors,
  saving,
  submitError,
  asesorNombre,
  today,
  onChange,
  onSubmit,
}) {
  const terminales = contexto ? terminalesDelComercio(contexto.terminales, numeroComercio) : [];
  const ultima = contexto?.ultimaEntrega;
  const inputProps = (key) => ({
    id: fieldId(key),
    value: form[key],
    onChange: (event) => onChange(key, event.target.value),
    ...describedBy(errors, key),
  });

  return (
    <>
      {contexto ? <Contexto contexto={contexto} /> : <ComercioPendiente />}
      {submitError ? (
        <Alert tone="error" role="alert" title={submitError.title}>
          {submitError.text}
        </Alert>
      ) : null}
      <form
        className="crm-rollo__form-wrap"
        noValidate
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit();
        }}
      >
        <fieldset className="crm-rollo__form" disabled={saving}>
          <legend className="crm-rollo__sr-only">Datos de la gestión</legend>
          <Field name="terminal" label="Terminal" htmlFor={fieldId("terminal")} optional errors={errors} help="Podés crear la gestión sin terminal.">
            <span className="crm-rollo__field crm-rollo__field--select">
              <select className="crm-rollo__select" {...inputProps("terminal")}>
                <option value="">Sin terminal</option>
                {terminales.map((terminal) => (
                  <option key={terminal.numeroTerminal} value={terminal.numeroTerminal}>
                    {terminalOptionLabel(terminal)}
                  </option>
                ))}
              </select>
            </span>
          </Field>

          <Field
            name="cantidad"
            label="Cantidad de rollos pedida"
            htmlFor={fieldId("cantidad")}
            required
            errors={errors}
            help={ultima ? `La última vez pidió ${formatRollos(ultima.cantidad)}` : null}
          >
            <input
              className="crm-rollo__input"
              type="number"
              min={LIMITS.cantidadMin}
              max={LIMITS.cantidadMax}
              step="1"
              inputMode="numeric"
              {...inputProps("cantidad")}
            />
          </Field>

          <Field name="canal" label="Canal del pedido" labelId="crm-rollo-nueva-canal-label" required full errors={errors}>
            <ChoiceGroup
              id={fieldId("canal")}
              name="crm-rollo-nueva-canal"
              options={CANAL_OPTIONS}
              value={form.canal}
              onChange={(value) => onChange("canal", value)}
              labelledBy="crm-rollo-nueva-canal-label"
              describedBy={errors.canal ? errorId("canal") : undefined}
              invalid={Boolean(errors.canal)}
            />
          </Field>

          <Field name="sinRollos" label="¿Se quedó sin rollos?" labelId="crm-rollo-nueva-sin-label" full errors={errors}>
            <ChoiceGroup
              name="crm-rollo-nueva-sin"
              options={SIN_ROLLOS_OPTIONS}
              value={form.sinRollos}
              onChange={(value) => onChange("sinRollos", value)}
              labelledBy="crm-rollo-nueva-sin-label"
            />
          </Field>
          {form.sinRollos === "SI" ? (
            <div className="crm-rollo__fld--full">
              <Alert tone="high" role="alert" title="Se creará con prioridad Alta" />
            </div>
          ) : null}

          <Field
            name="direccion"
            label="Dirección de entrega"
            htmlFor={fieldId("direccion")}
            full
            errors={errors}
            help="Precargada del legajo. Si la cambiás, solo se usa para este pedido."
          >
            <input className="crm-rollo__input" type="text" autoComplete="off" {...inputProps("direccion")} />
          </Field>

          <div className={`crm-rollo__fld crm-rollo__fld--full${errors.contactoNombre || errors.contactoTelefono ? " has-error" : ""}`}>
            <span className="crm-rollo__fld-label">Contacto que recibe</span>
            <div className="crm-rollo__pair">
              <input
                className="crm-rollo__input"
                type="text"
                autoComplete="off"
                placeholder="Nombre y apellido"
                aria-label="Nombre de quien recibe"
                {...inputProps("contactoNombre")}
              />
              <input
                className="crm-rollo__input"
                type="tel"
                autoComplete="off"
                placeholder="Teléfono"
                aria-label="Teléfono de quien recibe"
                {...inputProps("contactoTelefono")}
              />
            </div>
            <ErrorText errors={errors} name="contactoNombre" />
            <ErrorText errors={errors} name="contactoTelefono" />
          </div>

          <Field name="fecha" label="Fecha estimada de entrega" htmlFor={fieldId("fecha")} optional errors={errors}>
            <input className="crm-rollo__input" type="date" min={today || undefined} {...inputProps("fecha")} />
          </Field>

          <Field name="observacion" label="Observación" htmlFor={fieldId("observacion")} optional full errors={errors}>
            <textarea className="crm-rollo__input crm-rollo__textarea" rows={3} {...inputProps("observacion")} />
          </Field>
        </fieldset>
      </form>
      <DatosAutomaticos asesorNombre={asesorNombre} today={today} />
    </>
  );
}
